import { useMemo } from 'react';

import { useMutation } from '@tanstack/react-query';

import { stripeService, userService } from '@chartai/firebase';

import { useUserDocument } from './useUser';

interface SubscriptionStatusResult {
  isActive: boolean;
  isTrialing: boolean;
  isCanceled: boolean;
  isExpired: boolean;
  trialDaysLeft: number | null;
  trialExpiryDate: Date | null;
  subscriptionEndDate: Date | null;
  subscriptionTier: string | null;
  status: string | null;
  stripeCustomerId: string | null;
  isLoading: boolean;
}

function parseDate(value: unknown): Date | null {
  if (!value) {
    return null;
  }
  if (typeof value === 'string' || typeof value === 'number') {
    const d = new Date(value);
    return isNaN(d.getTime()) ? null : d;
  }
  if (typeof value === 'object' && value !== null) {
    const rec = value as Record<string, unknown>;
    if ('toDate' in value && typeof rec['toDate'] === 'function') {
      return (rec['toDate'] as () => Date)();
    }
    if ('seconds' in value) {
      return new Date((value as { seconds: number }).seconds * 1000);
    }
  }
  return null;
}

function normalizeStatus(raw: unknown): string | null {
  if (typeof raw !== 'string' || !raw) {
    return null;
  }
  const lower = raw.toLowerCase();
  return lower === 'cancelled' ? 'canceled' : lower;
}

const ACTIVE_STATUSES = new Set(['active', 'trialing', 'paid', 'ok']);

export function useSubscriptionStatus(uid: string): SubscriptionStatusResult {
  const { data: userDoc, isLoading } = useUserDocument(uid || null);

  return useMemo(() => {
    if (!userDoc) {
      return {
        isActive: false,
        isTrialing: false,
        isCanceled: false,
        isExpired: false,
        trialDaysLeft: null,
        trialExpiryDate: null,
        subscriptionEndDate: null,
        subscriptionTier: null,
        status: null,
        stripeCustomerId: null,
        isLoading,
      };
    }

    const sub =
      typeof userDoc['subscription'] === 'object' && userDoc['subscription'] !== null
        ? (userDoc['subscription'] as Record<string, unknown>)
        : null;

    const rawStatus = sub?.['status'] ?? userDoc['subscriptionStatus'] ?? null;
    const status = normalizeStatus(rawStatus);

    const isActive = status !== null && ACTIVE_STATUSES.has(status);
    const isTrialing = status === 'trialing';
    // Stripe sets status='active' + cancelAtPeriodEnd=true when cancel-at-period-end
    // is scheduled. Only flips to status='canceled' after the period actually ends.
    const cancelAtPeriodEnd = sub?.['cancelAtPeriodEnd'] === true;
    const isCanceled = status === 'canceled' || cancelAtPeriodEnd;

    const trialExpiryDate = parseDate(userDoc['trialExpiryDate']);
    let trialDaysLeft: number | null = null;
    if (trialExpiryDate) {
      const diffMs = trialExpiryDate.getTime() - Date.now();
      trialDaysLeft = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
    }

    // CAB-526: trial expiry no longer gates the app. Every plan (including Free)
    // grants full feature access — only the monthly usage-minute allowance differs
    // between plans. So `isExpired` must never block the frontend: an expired-trial
    // user on the Free plan was being bounced from /dashboard/profiles back to
    // /subscriptions by AuthGuard's feature gate. Force it false everywhere; the
    // trialExpiryDate/trialDaysLeft fields remain for informational display only.
    const isExpired = false;

    const subscriptionEndDate =
      parseDate(sub?.['currentPeriodEnd']) ?? parseDate(sub?.['expiresAt']);
    const subscriptionTier =
      typeof userDoc['subscriptionTier'] === 'string' ? userDoc['subscriptionTier'] : null;
    const stripeCustomerId =
      typeof userDoc['stripeCustomerId'] === 'string' ? userDoc['stripeCustomerId'] : null;

    return {
      isActive,
      isTrialing,
      isCanceled,
      isExpired,
      trialDaysLeft,
      trialExpiryDate,
      subscriptionEndDate,
      subscriptionTier,
      status,
      stripeCustomerId,
      isLoading,
    };
  }, [userDoc, isLoading]);
}

export function useEnsureStripeCustomer() {
  return useMutation({
    mutationFn: async (input: { apiUrl: string; uid: string; name: string; email: string }) => {
      const customerId = await stripeService.createCustomer(input.apiUrl, input.name, input.email);
      await userService.updateUser(input.uid, { stripeCustomerId: customerId });
      return customerId;
    },
  });
}

export function useCreateCheckoutSession() {
  return useMutation({
    mutationFn: async (input: {
      apiUrl: string;
      successUrl: string;
      cancelUrl: string;
      priceId: string;
      customerId: string;
      userId?: string;
    }) => {
      return stripeService.createCheckoutSession(
        input.apiUrl,
        input.successUrl,
        input.cancelUrl,
        input.priceId,
        input.customerId,
        input.userId,
      );
    },
  });
}

export function useCreateBillingPortalSession() {
  return useMutation({
    mutationFn: async (input: { apiUrl: string; returnUrl: string }) => {
      // The Stripe customer is resolved server-side from the authenticated uid;
      // the client intentionally does not pass a customer id (IDOR-safe).
      return stripeService.createBillingPortalSession(input.apiUrl, input.returnUrl);
    },
  });
}

export function useCancelSubscription() {
  return useMutation({
    mutationFn: async (input: { apiUrl: string; stripeSubscriptionId: string }) => {
      await stripeService.cancelSubscription(input.apiUrl, input.stripeSubscriptionId);
    },
  });
}

export function useResumeSubscription() {
  return useMutation({
    mutationFn: async (input: { apiUrl: string; stripeSubscriptionId: string }) => {
      await stripeService.resumeSubscription(input.apiUrl, input.stripeSubscriptionId);
    },
  });
}

export function useUpdateSubscription() {
  return useMutation({
    mutationFn: async (input: {
      apiUrl: string;
      stripeSubscriptionId: string;
      stripeSubscriptionItemId: string;
      newPriceId: string;
      isDowngrade: boolean;
    }) => {
      await stripeService.updateSubscription(
        input.apiUrl,
        input.stripeSubscriptionId,
        input.stripeSubscriptionItemId,
        input.newPriceId,
        input.isDowngrade,
      );
    },
  });
}
